import { useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatDate, formatDateTime } from "../../utils/date";
import { selectAccessToken } from "../../store/slices/authSlice";
import EventStatusChip from "./EventStatusChip";
import {
  openDetailDialog,
  selectEventItems,
  selectEventsSort,
  selectStatusUpdateMap,
  setSort,
  updateEventStatus,
} from "../../store/slices/eventsSlice";
import {
  forceLogoutToLogin,
  isUnauthorizedApiResponse,
} from "../../utils/unauthorized";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

const columns = [
  { id: "id", label: "ID", width: 80 },
  { id: "name", label: "Name", minWidth: 200 },
  { id: "start_datetime", label: "Start", minWidth: 180 },
  { id: "end_datetime", label: "End", minWidth: 180 },
  { id: "status", label: "Status", width: 120 },
  { id: "event_rule", label: "Rule", width: 160 },
  { id: "limit_per_ticket", label: "Limit/Ticket", width: 140 },
  { id: "created_datetime", label: "Created", minWidth: 160 },
  { id: "updated_datetime", label: "Updated", minWidth: 160 },
];

const EventTable = ({ dense = false }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const headerGradient =
    "linear-gradient(90deg, #030712 0%, #0f172a 50%, #111827 100%)";
  const navigate = useNavigate();
  const rows = useSelector(selectEventItems);
  const sort = useSelector(selectEventsSort);
  const statusUpdates = useSelector(selectStatusUpdateMap);
  const accessToken = useSelector(selectAccessToken);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmReset, setConfirmReset] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [resetMessage, setResetMessage] = useState(null);
  const [downloadingMap, setDownloadingMap] = useState({});
  const [resettingMap, setResettingMap] = useState({});

  const handleSort = (columnId) => {
    const isAsc =
      sort.field === columnId && (!sort.direction || sort.direction === "asc");
    dispatch(
      setSort({
        field: columnId,
        direction: isAsc ? "desc" : "asc",
      })
    );
  };
  const renderCellValue = (row, column) => {
    const value = row[column.id];
    if (column.id === "status") {
      return <EventStatusChip status={value} />;
    }
    if (column.id === "start_datetime" || column.id === "end_datetime") {
      return formatDate(value);
    }
    if (column.id.includes("datetime")) {
      return formatDateTime(value);
    }
    if (column.id === "limit_per_ticket") {
      return value === 0 ? "Unlimited" : value;
    }
    if (column.id === "event_rule") {
      return value === "GBK_LOCKED" ? "GBK Locked" : "No Restriction";
    }
    return value || "-";
  };

  const canDownloadOdReport = (row) => row.status === "ACTIVE";

  const getAttachmentFilename = (response, fallback) => {
    const disposition = response.headers.get("content-disposition");
    if (!disposition) return fallback;

    const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
      return decodeURIComponent(utfMatch[1]);
    }

    const plainMatch = disposition.match(/filename="?([^"]+)"?/i);
    return plainMatch?.[1] || fallback;
  };

  const handleDownloadOdReport = async (row) => {
    setDownloadError(null);
    setDownloadingMap((current) => ({ ...current, [row.id]: true }));

    try {
      const response = await fetch(`${API_BASE_URL}/events/${row.id}/od-report`, {
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });

      if (isUnauthorizedApiResponse(response)) {
        forceLogoutToLogin();
        return;
      }

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (isUnauthorizedApiResponse(response, payload)) {
          forceLogoutToLogin();
          return;
        }

        throw new Error(payload?.resultDesc || "Failed to download OD report.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getAttachmentFilename(
        response,
        `event-${row.id}-od-report.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(error.message || "Failed to download OD report.");
    } finally {
      setDownloadingMap((current) => ({ ...current, [row.id]: false }));
    }
  };

  const handleResetTickets = async (row) => {
    setDownloadError(null);
    setResetMessage(null);
    setResettingMap((current) => ({ ...current, [row.id]: true }));

    try {
      const response = await fetch(`${API_BASE_URL}/events/${row.id}/tickets/reset`, {
        method: "POST",
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });

      if (isUnauthorizedApiResponse(response)) {
        forceLogoutToLogin();
        return;
      }

      const payload = await response.json().catch(() => null);

      if (isUnauthorizedApiResponse(response, payload)) {
        forceLogoutToLogin();
        return;
      }

      if (!response.ok || payload?.resultCode !== 200) {
        throw new Error(payload?.resultDesc || "Failed to reset event tickets.");
      }

      const ticketsReset =
        payload?.data?.ticketsReset ?? payload?.ticketsReset ?? 0;

      setResetMessage(
        `Successfully reset ${ticketsReset} ticket(s) for event #${row.id}.`
      );
    } catch (error) {
      setDownloadError(error.message || "Failed to reset event tickets.");
    } finally {
      setResettingMap((current) => ({ ...current, [row.id]: false }));
    }
  };

  return (
    <>
      {resetMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {resetMessage}
        </Alert>
      )}
      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {downloadError}
        </Alert>
      )}
      <TableContainer
        sx={{
          display: "inline-block",
          minWidth: "100%",
          width: "100%",
        }}
      >
        <Table
          size={dense ? "small" : "medium"}
          sx={{ minWidth: 1100, borderCollapse: "separate", borderSpacing: 0 }}
        >
          <TableHead
            sx={{
              background: headerGradient,
              "& th": {
                borderBottom: "none",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: 0.3,
                color: theme.palette.common.white,
                "& .MuiTableSortLabel-icon": {
                  color: `${theme.palette.common.white} !important`,
                },
                "& .MuiTableSortLabel-root": {
                  color: "inherit",
                  "&.Mui-active": {
                    color: theme.palette.common.white,
                  },
                },
              },
              "& th:first-of-type": {
                borderTopLeftRadius: 10,
              },
              "& th:last-of-type": {
                borderTopRightRadius: 10,
              },
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sortDirection={sort.field === column.id ? sort.direction : false}
                  sx={{ minWidth: column.minWidth, width: column.width }}
                >
                  <TableSortLabel
                    active={sort.field === column.id}
                    direction={sort.field === column.id ? sort.direction : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell width={180} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                hover
                tabIndex={-1}
                key={row.id}
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(15, 23, 42, 0.02)",
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell key={`${row.id}-${column.id}`}>
                    {renderCellValue(row, column)}
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="View details">
                      <IconButton onClick={() => dispatch(openDetailDialog(row.id))}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit event">
                      <IconButton onClick={() => navigate(`/events/${row.id}/edit`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset tickets">
                      <span>
                        <IconButton
                          onClick={() => setConfirmReset(row)}
                          disabled={Boolean(resettingMap[row.id])}
                        >
                          <RestartAltIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {canDownloadOdReport(row) && (
                      <Tooltip
                        title={
                          downloadingMap[row.id]
                            ? "Downloading OD report..."
                            : "Download OD report"
                        }
                      >
                        <span>
                          <IconButton
                            onClick={() => handleDownloadOdReport(row)}
                            disabled={Boolean(downloadingMap[row.id])}
                          >
                            {downloadingMap[row.id] ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip
                      title={
                        row.status === "ACTIVE"
                          ? "Set as inactive"
                          : "Set as active"
                      }
                    >
                      <span>
                        <IconButton
                          color={row.status === "ACTIVE" ? "success" : "default"}
                          onClick={() =>
                            setConfirmAction({
                              id: row.id,
                              nextStatus:
                                row.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
                              label:
                                row.status === "ACTIVE"
                                  ? "inactive"
                                  : "active",
                            })
                          }
                          disabled={statusUpdates[row.id] === "loading"}
                        >
                          {row.status === "ACTIVE" ? (
                            <ToggleOnIcon fontSize="small" />
                          ) : (
                            <ToggleOffIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={columns.length + 1}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No events match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction
              ? `Set event #${confirmAction.id} as ${confirmAction.label}?`
              : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (confirmAction) {
                dispatch(
                  updateEventStatus({
                    id: confirmAction.id,
                    status: confirmAction.nextStatus,
                  })
                );
              }
              setConfirmAction(null);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(confirmReset)}
        onClose={() => setConfirmReset(null)}
      >
        <DialogTitle>Confirm Ticket Reset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmReset
              ? "Warning! This will delete all ticket transaction and reset the ticket usage on this event. Do you wish to continue?"
              : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReset(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if (confirmReset) {
                handleResetTickets(confirmReset);
              }
              setConfirmReset(null);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventTable;
